import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Panel, Pill } from "@/components/kit";
import { api, errMsg, gbp } from "@/lib/api";
import { FEATURE_LABELS } from "@/lib/seo";
import { FormDialog, SectionHead, Toggle } from "@/pages/manage/common";
import { PlanFeatures } from "@/pages/manage/Billing";

const BLANK = { name: "", description: "", price_monthly: "", price_yearly: "", features: {}, is_default: false, highlight: false, active: true, sort: 0 };

const PlanForm = ({ f, setF }) => (
  <>
    <Field label="Plan name"><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="plan-name-input" /></Field>
    <Field label="Description"><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="plan-description-input" /></Field>
    <div className="grid grid-cols-3 gap-4">
      <Field label="Monthly (£)"><Input type="number" step="0.01" min="0" value={f.price_monthly} onChange={(e) => setF({ ...f, price_monthly: e.target.value })} data-testid="plan-monthly-input" /></Field>
      <Field label="Yearly (£)"><Input type="number" step="0.01" min="0" value={f.price_yearly} onChange={(e) => setF({ ...f, price_yearly: e.target.value })} data-testid="plan-yearly-input" /></Field>
      <Field label="Order"><Input type="number" value={f.sort} onChange={(e) => setF({ ...f, sort: e.target.value })} data-testid="plan-sort-input" /></Field>
    </div>
    <div className="space-y-2 rounded-md border border-white/10 p-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Included features</p>
      {Object.entries(FEATURE_LABELS).map(([k, l]) => (
        <Toggle key={k} checked={!!f.features[k]} onChange={(v) => setF({ ...f, features: { ...f.features, [k]: v } })} label={l} testid={`plan-feature-${k}`} />
      ))}
    </div>
    <Toggle checked={f.is_default} onChange={(v) => setF({ ...f, is_default: v })} label="Default plan for teams without a subscription" testid="plan-default-toggle" />
    <Toggle checked={f.highlight} onChange={(v) => setF({ ...f, highlight: v })} label="Highlight as most popular" testid="plan-highlight-toggle" />
    <Toggle checked={f.active} onChange={(v) => setF({ ...f, active: v })} label="Visible to teams" testid="plan-active-toggle" />
  </>
);

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(BLANK);
  const load = () => api.get("/platform/plans").then((r) => setPlans(r.data));
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    const body = { ...f, price_monthly: Number(f.price_monthly || 0), price_yearly: Number(f.price_yearly || 0), sort: Number(f.sort || 0) };
    try {
      if (editing) await api.put(`/platform/plans/${editing.id}`, body);
      else await api.post("/platform/plans", body);
      toast.success("Plan saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete the ${p.name} plan?`)) return;
    try {
      await api.delete(`/platform/plans/${p.id}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6">
      <SectionHead title="Plans" text="What teams pay you, billed monthly or yearly via Stripe. Unpaid teams stay live on the default plan.">
        <Button className="rounded-full font-bold" onClick={() => { setEditing(null); setF(BLANK); setOpen(true); }} data-testid="add-plan-btn"><Plus className="mr-1 h-4 w-4" /> New plan</Button>
      </SectionHead>
      <div className="grid gap-5 lg:grid-cols-3">
        {plans.map((p) => (
          <Panel key={p.id} className="flex flex-col" data-testid={`admin-plan-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-display text-2xl font-black uppercase text-white">{p.name}</p>
                <div className="mt-1 flex flex-wrap gap-1">{p.is_default && <Pill tone="cyan">default</Pill>}{p.highlight && <Pill tone="volt"><Star className="h-3 w-3" />popular</Pill>}{!p.active && <Pill tone="red">hidden</Pill>}<Pill>{p.team_count} teams</Pill></div>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => { setEditing(p); setF(p); setOpen(true); }} data-testid={`edit-plan-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => del(p)} data-testid={`delete-plan-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <p className="mt-2 text-sm text-slate-400">{p.description}</p>
            <p className="mt-4 font-mono text-sm text-slate-200">{gbp(p.price_monthly)}/mo · {gbp(p.price_yearly)}/yr</p>
            <div className="mt-4"><PlanFeatures features={p.features} /></div>
          </Panel>
        ))}
      </div>
      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Edit plan" : "New plan"} onSubmit={save} testid="plan-form"><PlanForm f={f} setF={setF} /></FormDialog>
    </div>
  );
}
